import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AppFooter } from "@/components/layout/AppFooter";
import { AppLogo } from "@/components/ui/AppLogo";
import { applyTheme, getStoredTheme, playPortalTransition } from "@/lib/theme";
import { MobiAvatar } from "@/components/MobiAvatar";
import api from "@/lib/api";
import { DiskSpaceModal, SeatsModal } from "@/components/layout/PurchaseModals";
import { HelpVideoModal } from "@/components/HelpVideoModal";
import {
  LayoutDashboard, Users, CalendarClock, BookOpen, Library,
  Settings, LogOut, CreditCard, Bell, Menu, X, ShieldCheck, Inbox,
  HeartHandshake, Building2, Video, Sun, Moon, Palette, Sparkles,
  PenTool, Library as LibraryIcon, GraduationCap, Layers, ScrollText, ScanText, MessagesSquare, Crown, Zap, HardDrive, UserPlus,
  BarChart3, PlayCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const teacherNav = [
  { to: "/teacher", icon: LayoutDashboard, label: "Overview" },
  { to: "/teacher/classes", icon: CalendarClock, label: "My Classes" },
  { to: "/teacher/grades", icon: GraduationCap, label: "Grades" },
  { to: "/teacher/subjects", icon: Layers, label: "Subjects" },
  { to: "/teacher/skills", icon: Sparkles, label: "Skills", pill: "NEW" },
  { to: "/teacher/analytics", icon: BarChart3, label: "Analytics", pill: "NEW" },
  { to: "/teacher/students", icon: Users, label: "Students" },
  { to: "/teacher/recordings", icon: Video, label: "Recordings" },
  { to: "/teacher/whiteboard", icon: PenTool, label: "Interactive Board" },
  { to: "/teacher/ai-library", icon: LibraryIcon, label: "AI Library" },
  { to: "/teacher/bookshelf", icon: BookOpen, label: "Book Shelf (Coming Soon)" },
  { to: "/teacher/billing", icon: CreditCard, label: "Billing" },
  { to: "/teacher/settings", icon: Settings, label: "Settings" },
];

const studentNav = [
  { to: "/student", icon: LayoutDashboard, label: "Overview" },
  { to: "/student/classes", icon: CalendarClock, label: "Classes" },
  { to: "/student/recordings", icon: Video, label: "Recordings" },
  { to: "/student/quizzes", icon: ScrollText, label: "Quizzes" },
  { to: "/student/skills", icon: Sparkles, label: "Skills", pill: "NEW" },
  { to: "/student/screenshot-quiz", icon: ScanText, label: "Screenshot Quiz" },
  { to: "/student/ai-tutor", icon: MessagesSquare, label: "AI Tutor" },
  { to: "/student/library", icon: Library, label: "Library" },
  { to: "/student/bookshelf", icon: BookOpen, label: "Book Shelf (Coming Soon)" },
  { to: "/student/settings", icon: Settings, label: "Settings" },
];

const parentNav = [
  { to: "/parent", icon: HeartHandshake, label: "Overview" },
  { to: "/parent/recordings", icon: Video, label: "Recordings" },
  { to: "/parent/bookshelf", icon: BookOpen, label: "Book Shelf (Coming Soon)" },
  { to: "/parent/settings", icon: Settings, label: "Settings" },
];

const instituteNav = [
  { to: "/institute", icon: Building2, label: "Overview" },
  { to: "/institute/recordings", icon: Video, label: "Recordings" },
  { to: "/institute/bookshelf", icon: BookOpen, label: "Book Shelf (Coming Soon)" },
  { to: "/institute/settings", icon: Settings, label: "Settings" },
];

import { ClipboardList, UserCog, PhoneCall, Target } from "lucide-react";

const staffNav = [
  { to: "/staff", icon: PhoneCall, label: "Quality Surveys" },
];

const adminNav = [
  { to: "/admin", icon: ShieldCheck, label: "Admin Overview" },
  { to: "/admin/kpi", icon: Target, label: "Pilot KPIs" },
  { to: "/admin/surveys", icon: ClipboardList, label: "Surveys" },
  { to: "/admin/access", icon: UserCog, label: "Users & Access" },
  { to: "/admin/tool-requests", icon: Inbox, label: "Tool Requests" },
  // The teacher "Overview" page calls teacher-only APIs (403 for admins) —
  // admins get "Admin Overview" instead, so drop the duplicate entry.
  ...teacherNav.filter((i) => i.to !== "/teacher"),
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Interactive Board route → sidebar auto-collapses so the board gets the
  // full viewport width. The hamburger re-opens it as an overlay.
  const boardMode = location.pathname.endsWith("/whiteboard");
  const [open, setOpen] = useState(false);
  // Desktop slide-away — sidebar is collapsible everywhere via the hamburger;
  // it starts collapsed on the Interactive Board route, expanded elsewhere.
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => { setCollapsed(boardMode); setOpen(false); }, [boardMode]);
  const [storage, setStorage] = useState(null);
  const [seatCount, setSeatCount] = useState(null);
  const [purchaseModal, setPurchaseModal] = useState(null); // 'storage' | 'seats'
  const [helpVideo, setHelpVideo] = useState({ open: false, firstTime: false });
  // First login ever → auto-open the "how to use" video once (account-tracked)
  useEffect(() => {
    if (user?.role !== "teacher" || user?.help_video_seen) return;
    setHelpVideo({ open: true, firstTime: true });
    api.post("/auth/help-video-seen").catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  useEffect(() => {
    if (user?.role !== "teacher") return;
    api.get("/teacher/storage").then(({ data }) => setStorage(data)).catch(() => { });
    api.get("/students").then(({ data }) => setSeatCount((data.students || []).length)).catch(() => { });
  }, [user?.role]);
  const [theme, setTheme] = useState(getStoredTheme());
  const cycleTheme = () => {
    const next = theme === "cream" ? "light" : theme === "light" ? "dark" : theme === "dark" ? "neon" : "cream";
    if (next === "neon" || theme === "neon") {
      playPortalTransition(next, () => setTheme(next));
    } else {
      setTheme(next);
      applyTheme(next);
    }
  };
  useEffect(() => { applyTheme(theme); }, [theme]);

  const items =
    user?.role === "admin" ? adminNav :
    user?.role === "staff" ? staffNav :
    user?.role === "teacher" ? teacherNav :
    user?.role === "parent" ? parentNav :
    user?.role === "institute" ? instituteNav :
    studentNav;

  return (
    <div className="min-h-screen themed-bg flex">
      {/* Sidebar — light, matches landing */}
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 w-64 themed-sidebar border-r themed-border z-40 transform transition-transform ${collapsed ? "lg:-translate-x-full" : "lg:translate-x-0"} ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="h-16 px-5 flex items-center border-b themed-border">
          <AppLogo size="md" to={user?.role === "student" ? "/student" : user?.role === "parent" ? "/parent" : user?.role === "admin" ? "/admin" : user?.role === "institute" ? "/institute" : user?.role === "staff" ? "/staff" : "/teacher"} />
          <button className="ml-auto text-[#0A0C10] p-1 hover:bg-black/5 rounded-md transition-colors" onClick={() => { setOpen(false); setCollapsed(true); }} aria-label="Close menu" title="Collapse the menu" data-testid="sidebar-close-btn"><X size={18} /></button>
        </div>
        <nav className="px-3 py-4 space-y-0.5 overflow-y-auto max-h-[calc(100vh-9rem)]">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2.5 text-[13.5px] font-medium rounded-md transition-all duration-200 ${isActive ? "bg-[#0A0C10] text-[#FBFAF7] shadow-[0_8px_22px_-10px_rgba(10,12,16,0.45)]" : "text-neutral-600 hover:bg-black/5 hover:text-[#0A0C10]"}`
              }
            >
              <it.icon size={17} strokeWidth={1.6} />
              <span className="flex-1">{it.label}</span>
              {it.pill && (
                <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFD600] text-[#0A0C10] font-bold">{it.pill}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-black/8 p-4 bg-[#FAF9F6]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#0A0C10] text-[#FBFAF7] flex items-center justify-center font-semibold">
              {user?.full_name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate text-[#0A0C10]">{user?.full_name}</div>
              <div className="text-xs text-neutral-500 capitalize">{user?.role} · {user?.plan || "trial"}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); nav("/"); }}
            className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2 border border-black/10 hover:border-[#0A0C10] text-neutral-700 hover:text-[#0A0C10] rounded-md transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className={`flex-1 ${collapsed ? "" : "lg:ml-64"} min-w-0 flex flex-col transition-[margin] duration-200`}>
        {/* Top bar */}
        <header className="h-16 px-4 lg:px-8 themed-bg/85 backdrop-blur-xl border-b themed-border flex items-center sticky top-0 z-30">
          <button className="mr-3 p-1 hover:bg-black/5 rounded-md transition-colors" onClick={() => { setOpen((o) => !o); setCollapsed((c) => !c); }} aria-label="Toggle menu" title="Show / hide the menu" data-testid="sidebar-hamburger"><Menu size={20} /></button>
          <div className="flex-1 flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">
              <span>Operating console</span>
              <span className="w-1 h-1 bg-[#FFB400]" />
              <span>{user?.role}</span>
            </div>
            <DateTimeChip />
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "teacher" && (
              <button
                data-testid="video-help-btn"
                onClick={() => setHelpVideo({ open: true, firstTime: false })}
                title="Watch the video guide — how to use the platform"
                className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 text-white bg-gradient-to-r from-[#7C3AED] to-[#22D3EE] shadow-[0_0_14px_-2px_rgba(34,211,238,0.6)] hover:scale-105 transition-transform"
              >
                <PlayCircle size={14} /> <span className="hidden sm:inline">Video Help</span>
              </button>
            )}
            {user?.role === "teacher" && (
              <>
                <button
                  data-testid="disk-space-btn"
                  onClick={() => setPurchaseModal("storage")}
                  title="Disk space used — click + to buy more"
                  className="hidden lg:inline-flex items-center gap-1.5 text-xs font-semibold border border-black/15 hover:border-[#0A0C10] rounded-full px-3 py-1 transition-colors"
                >
                  <HardDrive size={12} className="text-[#22D3EE]" />
                  Disk Space: {storage ? `${Math.round(storage.used_mb)} MB / ${storage.quota_mb} MB` : "…"}
                  <span className="w-4 h-4 rounded-full bg-[#22D3EE]/15 text-[#22D3EE] font-bold inline-flex items-center justify-center leading-none">+</span>
                </button>
                <button
                  data-testid="student-seats-btn"
                  onClick={() => setPurchaseModal("seats")}
                  title="Student seats used — click + to buy more"
                  className="hidden lg:inline-flex items-center gap-1.5 text-xs font-bold border border-[#22D3EE]/60 bg-[#22D3EE]/10 hover:bg-[#22D3EE]/25 rounded-full px-3 py-1 transition-colors"
                >
                  <UserPlus size={12} className="text-[#22D3EE]" />
                  Student Seats {seatCount === null ? "…" : `${seatCount} / ${user?.student_limit ?? "—"}`}
                  <span className="w-4 h-4 rounded-full bg-[#22D3EE]/25 text-[#22D3EE] font-bold inline-flex items-center justify-center leading-none">+</span>
                </button>
              </>
            )}
            {user?.role === "teacher" && (
              <button
                data-testid="subscription-badge"
                onClick={() => nav("/teacher/billing")}
                title={`${user?.plan || "trial"} plan · up to ${user?.student_limit ?? "—"} students · ${user?.ai_credits ?? 0} AI credits — click to manage`}
                className="hidden lg:inline-flex items-center gap-1.5 text-xs font-semibold border border-black/15 hover:border-[#0A0C10] rounded-full px-3 py-1 capitalize transition-colors"
              >
                <Crown size={12} className="text-[#FFB400]" /> {user?.plan || "trial"} plan
              </button>
            )}
            <Badge data-testid="ai-credits-badge" variant="outline" className="border-[#FFB400] bg-[#FFB400]/10 text-[#0A0C10] font-semibold gap-1.5 rounded-full px-3">
              <Sparkles size={12} className="text-[#FFB400]" /> {user?.ai_credits ?? 0} credits
            </Badge>
            <button
              data-testid="theme-toggle"
              onClick={cycleTheme}
              aria-label={`Theme: ${theme}`}
              title={`Theme: ${theme} — click to cycle`}
              className="p-2 hover:bg-black/5 rounded-md transition-colors"
            >
              {theme === "neon" ? <Zap size={18} strokeWidth={1.6} className="text-[#22D3EE] drop-shadow-[0_0_8px_rgba(34,211,238,0.9)]" /> : theme === "dark" ? <Moon size={18} strokeWidth={1.6} /> : theme === "light" ? <Sun size={18} strokeWidth={1.6} /> : <Palette size={18} strokeWidth={1.6} />}
            </button>
            <button data-testid="notification-bell" aria-label="Notifications" className="p-2 hover:bg-black/5 rounded-md">
              <Bell size={18} strokeWidth={1.6} />
            </button>
            <MobiAvatar />
          </div>
        </header>

        {/* Page content */}
        <main className={boardMode ? "flex-1 w-full" : "px-4 lg:px-8 py-6 max-w-[1400px] flex-1 w-full"}>
          <Outlet />
        </main>
        {purchaseModal === "storage" && (
          <DiskSpaceModal onClose={() => setPurchaseModal(null)} usedMb={storage?.used_mb} quotaMb={storage?.quota_mb} />
        )}
        {purchaseModal === "seats" && (
          <SeatsModal onClose={() => setPurchaseModal(null)} current={seatCount ?? 0} limit={user?.student_limit ?? 5} />
        )}

        <HelpVideoModal
          open={helpVideo.open}
          firstTime={helpVideo.firstTime}
          onClose={() => setHelpVideo({ open: false, firstTime: false })}
        />

        {/* Footer */}
        {/* Footer — hidden in board mode so the canvas gets full height */}
        {!boardMode && <AppFooter variant="light" compact />}
      </div>
    </div>
  );
}

function DateTimeChip() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30_000); return () => clearInterval(t); }, []);
  const day = now.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).toUpperCase();
  const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return (
    <div
      data-testid="header-datetime"
      className="hidden sm:inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white shadow-[0_2px_10px_-2px_rgba(0,0,0,0.06)] border border-black/5"
    >
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FFB400] flex items-center justify-center text-[#0A0C10] text-[11px] font-bold">
        ☀
      </div>
      <div className="leading-tight">
        <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-neutral-500">{day}</div>
        <div className="text-[12px] font-bold text-[#0A0C10] -mt-0.5">{time}</div>
      </div>
    </div>
  );
}

